import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

interface ZoomableImageProps {
  src: string;
  alt?: string;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const WHEEL_ZOOM_STEP = 0.1;
const PAN_STEP = 50;

export function ZoomableImage({ src, alt = "" }: ZoomableImageProps) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const positionStart = useRef({ x: 0, y: 0 });
  const touchDistStart = useRef(0);
  const touchZoomStart = useRef(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when src changes (navigating between images)
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [src]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const newZoom = Math.max(z - ZOOM_STEP, MIN_ZOOM);
      if (newZoom === MIN_ZOOM) setPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "+":
        case "=":
          e.preventDefault();
          zoomIn();
          break;
        case "-":
          e.preventDefault();
          zoomOut();
          break;
        case "0":
          e.preventDefault();
          resetZoom();
          break;
        case "ArrowUp":
          if (zoom > 1) {
            e.preventDefault();
            setPosition((p) => ({ ...p, y: p.y + PAN_STEP }));
          }
          break;
        case "ArrowDown":
          if (zoom > 1) {
            e.preventDefault();
            setPosition((p) => ({ ...p, y: p.y - PAN_STEP }));
          }
          break;
        case "ArrowLeft":
          if (zoom > 1) {
            e.preventDefault();
            setPosition((p) => ({ ...p, x: p.x + PAN_STEP }));
          }
          break;
        case "ArrowRight":
          if (zoom > 1) {
            e.preventDefault();
            setPosition((p) => ({ ...p, x: p.x - PAN_STEP }));
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoom, zoomIn, zoomOut, resetZoom]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -WHEEL_ZOOM_STEP : WHEEL_ZOOM_STEP;
    setZoom((z) => {
      const newZoom = Math.max(MIN_ZOOM, Math.min(z + delta, MAX_ZOOM));
      if (newZoom === MIN_ZOOM) setPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }, []);

  // Mouse drag to pan
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      e.preventDefault();
      setIsDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      positionStart.current = { ...position };
    },
    [zoom, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: positionStart.current.x + (e.clientX - dragStart.current.x),
        y: positionStart.current.y + (e.clientY - dragStart.current.y),
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch: pinch-to-zoom + single-finger pan
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // Pinch start
        touchDistStart.current = getTouchDistance(e.touches);
        touchZoomStart.current = zoom;
      } else if (e.touches.length === 1 && zoom > 1) {
        // Single finger pan start
        setIsDragging(true);
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        positionStart.current = { ...position };
      }
    },
    [zoom, position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // Pinch zoom
        e.preventDefault();
        const dist = getTouchDistance(e.touches);
        const scale = dist / touchDistStart.current;
        const newZoom = Math.max(MIN_ZOOM, Math.min(touchZoomStart.current * scale, MAX_ZOOM));
        setZoom(newZoom);
        if (newZoom === MIN_ZOOM) setPosition({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && isDragging) {
        // Single finger pan
        setPosition({
          x: positionStart.current.x + (e.touches[0].clientX - dragStart.current.x),
          y: positionStart.current.y + (e.touches[0].clientY - dragStart.current.y),
        });
      }
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Double click/tap to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom > 1) {
      resetZoom();
    } else {
      setZoom(2);
    }
  }, [zoom, resetZoom]);

  return (
    <div className="flex flex-col items-center gap-2 w-full h-full">
      {/* Image container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden flex items-center justify-center min-h-0 w-full touch-none"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in" }}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-[70vh] object-contain rounded select-none"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transition: isDragging ? "none" : "transform 0.15s ease",
          }}
          draggable={false}
          onDoubleClick={handleDoubleClick}
        />
      </div>

      {/* Zoom controls */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
          title="Zoom out (−)"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
          title="Zoom in (+)"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        {zoom > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetZoom}
            title="Reset zoom (0)"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
